import { useEffect, useRef, useState } from "react";
import { Play } from "lucide-react";
import { Reveal, Chapter } from "@/components/portfolio/motion";

const TRAILERS = [
  {
    title: "Becoming Pablo — Pre-launch trailer",
    views: "~25K views",
    id: "HT19kmhqSiY",
  },
  {
    title: "City Block Builder — Teaser trailer",
    views: "~210K views",
    id: "sDleew0XfZ0",
  },
];

const TrailerCard = ({ t, i, active }) => {
  const [play, setPlay] = useState(false);
  const show = play || active;
  return (
    <Reveal delay={i * 0.08}>
      <figure
        data-testid={`trailer-card-${i}`}
        className="group rounded-3xl border border-line bg-surface overflow-hidden transition-all duration-300 hover:border-accent/50 hover:shadow-[0_30px_60px_-32px_rgba(23,22,15,0.35)]"
      >
        <div className="relative aspect-video bg-ink/5">
          {show ? (
            <iframe
              data-testid={`trailer-iframe-${i}`}
              className="absolute inset-0 w-full h-full"
              src={`https://www.youtube-nocookie.com/embed/${t.id}?rel=0&modestbranding=1${
                play ? "&autoplay=1" : ""
              }`}
              title={t.title}
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <button
              data-testid={`trailer-facade-${i}`}
              onClick={() => setPlay(true)}
              className="absolute inset-0 w-full h-full"
              aria-label={`Play ${t.title}`}
            >
              <img
                src={`https://img.youtube.com/vi/${t.id}/hqdefault.jpg`}
                alt={t.title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <span className="absolute inset-0 bg-ink/10 group-hover:bg-ink/20 transition-colors" />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-surface/90 backdrop-blur-sm ring-1 ring-line shadow-lg group-hover:scale-105 transition-transform">
                  <Play className="w-6 h-6 text-accent translate-x-0.5" fill="currentColor" />
                </span>
              </span>
            </button>
          )}
        </div>
        <figcaption className="flex items-start justify-between gap-4 p-6">
          <h3 className="font-display text-lg font-semibold text-ink leading-snug">
            {t.title}
          </h3>
          <span className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-line px-3 py-1 font-mono text-xs text-ink-soft whitespace-nowrap">
            <span className="w-1.5 h-1.5 rounded-full bg-accent" />
            {t.views}
          </span>
        </figcaption>
      </figure>
    </Reveal>
  );
};

export const Trailers = () => {
  const ref = useRef(null);
  const [near, setNear] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setNear(true);
          obs.disconnect();
        }
      },
      { rootMargin: "300px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      id="trailers"
      ref={ref}
      data-testid="trailers-section"
      className="scroll-mt-20 max-w-6xl mx-auto px-5 sm:px-8 py-20 sm:py-28 border-t border-line"
    >
      <Chapter num="03 / Trailers" label="Trailers" testId="trailers-chapter" />
      <Reveal>
        <p className="max-w-2xl -mt-6 mb-12 text-lg text-ink-soft leading-relaxed">
          A few of the trailers I've written, briefed, and run go-to-market
          around at Tentworks. Two are live here now, with more to come.
        </p>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {TRAILERS.map((t, i) => (
          <TrailerCard key={t.id} t={t} i={i} active={near} />
        ))}
      </div>
    </section>
  );
};
